import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * InstallPrompt — a low-intensity "Install Money Hero" pill that appears when
 * the browser fires `beforeinstallprompt`. Uses the standard PWA install flow
 * (no lib). One-click dismiss persists for 7 days via localStorage.
 */
const DISMISS_KEY = "mmh_install_dismissed_until";

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissedUntil = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedUntil > Date.now()) return;
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!visible || !deferred) return null;

  const install = async () => {
    try {
      deferred.prompt();
      await deferred.userChoice;
    } finally {
      setVisible(false);
      setDeferred(null);
    }
  };

  const dismiss = () => {
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    window.localStorage.setItem(DISMISS_KEY, String(Date.now() + sevenDays));
    setVisible(false);
  };

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-24 z-50 flex items-center gap-2 px-3 py-2 rounded-full bg-[#0E2924] border-2 border-[#F5D849]/60 shadow-lg"
      style={{ boxShadow: "0 0 22px rgba(245,216,73,0.25)" }}
      data-testid="install-prompt"
    >
      <button
        type="button"
        onClick={install}
        className="inline-flex items-center gap-1.5 text-[12px] font-black uppercase tracking-wider text-[#F5D849]"
        data-testid="install-prompt-accept"
        style={{ fontFamily: "Outfit, sans-serif" }}
      >
        <Download className="w-3.5 h-3.5" strokeWidth={2.8} />
        Install Money Hero
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="text-[#93B1A8] hover:text-[#F5D849]"
        aria-label="Dismiss install prompt"
        data-testid="install-prompt-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
